/**
 * Credit-grant object, frozen type templates, and the purpose-scoped
 * limit resolver (router#1998, epic #1997 E1).
 *
 * A grant is the single source of truth for a credit line. Long-term and
 * temporary grants share one table and one object; they differ only by
 * `grant_type`. The five behavioural parameters (validity, billing mode,
 * end condition, approval tier, and the fixed meaning of the amount as a
 * balance floor) are NOT stored per row and do NOT appear as UI switches:
 * they are looked up from {@link CREDIT_GRANT_TYPE_TEMPLATES}, a frozen
 * table keyed by type. An unknown type is refused. There is no "use the
 * newest template" fallback — the same rule `packages/firewall` uses for
 * doc versions.
 *
 * `temporary_bump` is reserved: the template slot exists so adding it later
 * is one more row (plus an explicit yes/no in every purpose map). Writes of
 * an unimplemented type are refused. Reads tolerate the row and apply the
 * purpose table (currently `false` everywhere, so a bump does not count).
 *
 * The amount on a grant is always a balance floor in integer micro-USD:
 * a $1,000 grant means the signed balance may go to −$1,000. Available
 * spend is `balance + resolved_limit`.
 */

// ─── Closed enumerations ───────────────────────────────────────────────────

export const CREDIT_GRANT_TYPES = ['long_term', 'temporary', 'temporary_bump'] as const;
export type CreditGrantType = (typeof CREDIT_GRANT_TYPES)[number];

export const CREDIT_GRANT_STATUSES = ['active', 'ended'] as const;
export type CreditGrantStatus = (typeof CREDIT_GRANT_STATUSES)[number];

export const CREDIT_GRANT_END_REASONS = [
  'expired',
  'repaid',
  'manual',
  'superseded',
] as const;
export type CreditGrantEndReason = (typeof CREDIT_GRANT_END_REASONS)[number];

/** Validity axis of a type template: no expiry, or a concrete end instant. */
export const CREDIT_VALIDITY_KINDS = ['none', 'dated'] as const;
export type CreditValidityKind = (typeof CREDIT_VALIDITY_KINDS)[number];

/** How a type enters accounts receivable. */
export const CREDIT_BILLING_MODES = [
  'monthly_recurring',
  'one_shot_on_expiry',
  'merge_into_monthly',
] as const;
export type CreditBillingMode = (typeof CREDIT_BILLING_MODES)[number];

/** When an active grant of this type is supposed to end. */
export const CREDIT_END_CONDITIONS = [
  'never',
  'balance_returns_positive',
  'expire_to_previous_limit',
] as const;
export type CreditEndCondition = (typeof CREDIT_END_CONDITIONS)[number];

/** Approval depth. Finance-single for every type this period. */
export const CREDIT_APPROVAL_TIERS = ['finance_single'] as const;
export type CreditApprovalTier = (typeof CREDIT_APPROVAL_TIERS)[number];

/**
 * Why a caller is asking for a limit. Required — never optional — so a
 * new grant type cannot be silently dropped from a consumer. Each purpose
 * must answer include/exclude for every {@link CreditGrantType}.
 */
export const CREDIT_LIMIT_PURPOSES = [
  'spendability',
  'monthly_statement',
  'statement_settlement',
  'ledger_overdraft',
  'gift_expiry',
  'notification',
  'runway',
  'profile',
] as const;
export type CreditLimitPurpose = (typeof CREDIT_LIMIT_PURPOSES)[number];

// ─── Type guards (no `as`, no default-to-latest) ───────────────────────────

export function isCreditGrantType(value: string): value is CreditGrantType {
  for (const candidate of CREDIT_GRANT_TYPES) {
    if (candidate === value) return true;
  }
  return false;
}

export function isCreditGrantStatus(value: string): value is CreditGrantStatus {
  for (const candidate of CREDIT_GRANT_STATUSES) {
    if (candidate === value) return true;
  }
  return false;
}

export function isCreditGrantEndReason(value: string): value is CreditGrantEndReason {
  for (const candidate of CREDIT_GRANT_END_REASONS) {
    if (candidate === value) return true;
  }
  return false;
}

export function isCreditLimitPurpose(value: string): value is CreditLimitPurpose {
  for (const candidate of CREDIT_LIMIT_PURPOSES) {
    if (candidate === value) return true;
  }
  return false;
}

export class UnknownCreditGrantTypeError extends Error {
  readonly grantType: string;
  constructor(grantType: string) {
    super(
      `unknown credit grant type ${JSON.stringify(grantType)}; ` +
        `known types: ${CREDIT_GRANT_TYPES.join(', ')}`,
    );
    this.name = 'UnknownCreditGrantTypeError';
    this.grantType = grantType;
  }
}

export class UnimplementedCreditGrantTypeError extends Error {
  readonly grantType: CreditGrantType;
  constructor(grantType: CreditGrantType) {
    super(
      `credit grant type ${grantType} is reserved and not implemented`,
    );
    this.name = 'UnimplementedCreditGrantTypeError';
    this.grantType = grantType;
  }
}

export class UnknownCreditLimitPurposeError extends Error {
  readonly purpose: string;
  constructor(purpose: string) {
    super(
      `unknown credit limit purpose ${JSON.stringify(purpose)}; ` +
        `known purposes: ${CREDIT_LIMIT_PURPOSES.join(', ')}`,
    );
    this.name = 'UnknownCreditLimitPurposeError';
    this.purpose = purpose;
  }
}

export class CreditLimitPurposeUndeclaredTypeError extends Error {
  readonly purpose: CreditLimitPurpose;
  readonly grantType: CreditGrantType;
  constructor(purpose: CreditLimitPurpose, grantType: CreditGrantType) {
    super(
      `credit limit purpose ${purpose} does not declare whether ${grantType} is included`,
    );
    this.name = 'CreditLimitPurposeUndeclaredTypeError';
    this.purpose = purpose;
    this.grantType = grantType;
  }
}

// ─── Frozen type templates ─────────────────────────────────────────────────

export interface CreditGrantTypeTemplate {
  readonly grantType: CreditGrantType;
  /** False = template slot only; writes of this type refuse. Reads tolerate it. */
  readonly implemented: boolean;
  readonly validity: CreditValidityKind;
  readonly billingMode: CreditBillingMode;
  readonly endCondition: CreditEndCondition;
  readonly approvalTier: CreditApprovalTier;
}

/**
 * Frozen `grant_type → 5 parameters` table. Adding a type means adding one
 * row here and answering that type in {@link CREDIT_LIMIT_PURPOSE_INCLUSION}.
 * Dispatch is by the type the row carries; there is no fallback.
 */
export const CREDIT_GRANT_TYPE_TEMPLATES: {
  readonly [K in CreditGrantType]: CreditGrantTypeTemplate;
} = Object.freeze({
  long_term: Object.freeze({
    grantType: 'long_term',
    implemented: true,
    validity: 'none',
    billingMode: 'monthly_recurring',
    endCondition: 'never',
    approvalTier: 'finance_single',
  }),
  temporary: Object.freeze({
    grantType: 'temporary',
    implemented: true,
    validity: 'dated',
    billingMode: 'one_shot_on_expiry',
    endCondition: 'balance_returns_positive',
    approvalTier: 'finance_single',
  }),
  temporary_bump: Object.freeze({
    grantType: 'temporary_bump',
    implemented: false,
    validity: 'dated',
    billingMode: 'merge_into_monthly',
    endCondition: 'expire_to_previous_limit',
    approvalTier: 'finance_single',
  }),
});

export function requireCreditGrantTypeTemplate(grantType: string): CreditGrantTypeTemplate {
  if (!isCreditGrantType(grantType)) {
    throw new UnknownCreditGrantTypeError(grantType);
  }
  return CREDIT_GRANT_TYPE_TEMPLATES[grantType];
}

export function requireImplementedCreditGrantType(grantType: string): CreditGrantType {
  const template = requireCreditGrantTypeTemplate(grantType);
  if (!template.implemented) {
    throw new UnimplementedCreditGrantTypeError(template.grantType);
  }
  return template.grantType;
}

// ─── Purpose → type inclusion (exhaustive) ─────────────────────────────────

/**
 * Every purpose answers every grant type. A missing key is a compile
 * error (`Record<CreditGrantType, boolean>`). A runtime hole — a purpose
 * object that somehow lacks a type — is an explicit refusal, never 0.
 *
 *   spendability          long_term + temporary (gateway can_spend)
 *   monthly_statement     long_term only (temporary must not enter AR)
 *   statement_settlement  both (one-shot bills, not the monthly scan)
 *   ledger_overdraft      both
 *   gift_expiry           both
 *   notification          both
 *   runway                both
 *   profile               both, returned separately by the resolver
 *
 * `temporary_bump` is answered `false` everywhere until the type is
 * implemented; flipping it later is an explicit edit of this table.
 */
export const CREDIT_LIMIT_PURPOSE_INCLUSION: {
  readonly [P in CreditLimitPurpose]: { readonly [K in CreditGrantType]: boolean };
} = Object.freeze({
  spendability: Object.freeze({
    long_term: true,
    temporary: true,
    temporary_bump: false,
  }),
  monthly_statement: Object.freeze({
    long_term: true,
    temporary: false,
    temporary_bump: false,
  }),
  statement_settlement: Object.freeze({
    long_term: true,
    temporary: true,
    temporary_bump: false,
  }),
  ledger_overdraft: Object.freeze({
    long_term: true,
    temporary: true,
    temporary_bump: false,
  }),
  gift_expiry: Object.freeze({
    long_term: true,
    temporary: true,
    temporary_bump: false,
  }),
  notification: Object.freeze({
    long_term: true,
    temporary: true,
    temporary_bump: false,
  }),
  runway: Object.freeze({
    long_term: true,
    temporary: true,
    temporary_bump: false,
  }),
  profile: Object.freeze({
    long_term: true,
    temporary: true,
    temporary_bump: false,
  }),
});

export function requireCreditLimitPurpose(purpose: string): CreditLimitPurpose {
  if (!isCreditLimitPurpose(purpose)) {
    throw new UnknownCreditLimitPurposeError(purpose);
  }
  return purpose;
}

/**
 * Grant types this purpose counts. Unknown purpose refuses. A purpose that
 * does not declare a type refuses. Unimplemented types are included when
 * the purpose answered true (none currently do) so the SQL IN-list and
 * the JS resolver stay on the same table. Writes of unimplemented types
 * are refused separately by {@link requireImplementedCreditGrantType}.
 */
export function creditGrantTypesForPurpose(purpose: CreditLimitPurpose): readonly CreditGrantType[] {
  const inclusion = CREDIT_LIMIT_PURPOSE_INCLUSION[purpose];
  if (inclusion === undefined) {
    throw new UnknownCreditLimitPurposeError(purpose);
  }
  const included: CreditGrantType[] = [];
  for (const grantType of CREDIT_GRANT_TYPES) {
    const declared = inclusion[grantType];
    if (declared === undefined) {
      throw new CreditLimitPurposeUndeclaredTypeError(purpose, grantType);
    }
    if (!declared) continue;
    included.push(grantType);
  }
  return included;
}

// ─── Grant snapshot + resolver ─────────────────────────────────────────────

export interface CreditGrantSnapshot {
  readonly grantType: string;
  readonly amountUsdMicros: number;
  readonly status: string;
  readonly startsAt: string | Date;
  readonly endsAt: string | Date | null;
}

export interface ResolvedCreditLimit {
  /** Sum of included, currently-effective grants. 0 when none. */
  readonly totalUsdMicros: number;
  /** Per-type totals for every implemented type this purpose includes. */
  readonly byType: Readonly<Partial<Record<CreditGrantType, number>>>;
}

function snapshotInstant(value: string | Date, field: string): number {
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  if (!Number.isFinite(ms)) {
    throw new Error(`credit grant ${field} is not a valid timestamp`);
  }
  return ms;
}

function grantIsEffectiveAt(grant: CreditGrantSnapshot, nowMs: number): boolean {
  if (grant.status !== 'active') return false;
  const startsAt = snapshotInstant(grant.startsAt, 'starts_at');
  if (startsAt > nowMs) return false;
  if (grant.endsAt === null) return true;
  return snapshotInstant(grant.endsAt, 'ends_at') > nowMs;
}

function requirePositiveGrantAmount(amountUsdMicros: number): number {
  if (!Number.isSafeInteger(amountUsdMicros) || amountUsdMicros <= 0) {
    throw new Error('credit grant amount must be a positive safe integer');
  }
  return amountUsdMicros;
}

/**
 * Resolve the credit limit that applies for `purpose`.
 *
 * Purpose is required. Unknown types refuse. An unimplemented type is
 * tolerated and counted only when the purpose table says so (currently
 * `false` for `temporary_bump` everywhere). Types the purpose declared
 * `false` are omitted. Types the purpose did not declare refuse. A
 * non-positive or non-integer amount is refused, not ignored.
 */
export function resolveCreditLimit(
  grants: readonly CreditGrantSnapshot[],
  purpose: CreditLimitPurpose,
  now: Date = new Date(),
): ResolvedCreditLimit {
  requireCreditLimitPurpose(purpose);
  const includedTypes = creditGrantTypesForPurpose(purpose);
  const nowMs = now.getTime();
  if (!Number.isFinite(nowMs)) {
    throw new Error('credit limit resolver now is not a valid timestamp');
  }

  const byType: Partial<Record<CreditGrantType, number>> = {};
  for (const grantType of includedTypes) {
    byType[grantType] = 0;
  }

  for (const grant of grants) {
    requirePositiveGrantAmount(grant.amountUsdMicros);
    const template = requireCreditGrantTypeTemplate(grant.grantType);
    const declared = CREDIT_LIMIT_PURPOSE_INCLUSION[purpose][template.grantType];
    if (declared === undefined) {
      throw new CreditLimitPurposeUndeclaredTypeError(purpose, template.grantType);
    }
    if (!declared) continue;
    if (!grantIsEffectiveAt(grant, nowMs)) continue;
    const current = byType[template.grantType] ?? 0;
    byType[template.grantType] = current + grant.amountUsdMicros;
  }

  let totalUsdMicros = 0;
  for (const grantType of includedTypes) {
    totalUsdMicros += byType[grantType] ?? 0;
  }
  return { totalUsdMicros, byType };
}

/**
 * SQL `IN (...)` list of grant types this purpose counts. Built from
 * {@link creditGrantTypesForPurpose} so a new type cannot appear in SQL
 * without an explicit purpose answer. Empty inclusion becomes `NULL`
 * (so `IN (NULL)` matches nothing) rather than a silent `0`.
 */
export function creditGrantTypesSqlInList(purpose: CreditLimitPurpose): string {
  const types = creditGrantTypesForPurpose(purpose);
  if (types.length === 0) return 'NULL';
  return types.map((grantType) => `'${grantType}'`).join(', ');
}

/**
 * Closed set of account-id expressions the SQL fragments will interpolate.
 * A user-looking id such as `acc_deadbeef` is not in this set and will not
 * type-check. Placeholders are `$1`… only — never a bound account id.
 */
export type CreditLimitAccountIdSql =
  | 'a.id'
  | 'accounts.id'
  | 'account_balances.account_id'
  | 'bt.account_id'
  | `$${number}`;

/**
 * Scalar subquery: SUM of currently-effective grant amounts for `purpose`.
 * `accountIdSql` must be a member of {@link CreditLimitAccountIdSql}.
 */
export function creditLimitSumSql(
  purpose: CreditLimitPurpose,
  accountIdSql: CreditLimitAccountIdSql,
  nowSql = 'CURRENT_TIMESTAMP',
): string {
  const typeList = creditGrantTypesSqlInList(purpose);
  const typeWindow = `(SELECT COALESCE(SUM(g.amount_usd_micros), 0)
             FROM credit_grants g
            WHERE g.account_id = ${accountIdSql}
              AND g.status = 'active'
              AND g.grant_type IN (${typeList})
              AND g.starts_at <= ${nowSql}
              AND (g.ends_at IS NULL OR g.ends_at > ${nowSql}))`;
  if (purpose !== 'spendability') return typeWindow;
  // Decision A / #2007: the purpose table still answers which grant types
  // count. D+7 overlays that answer for this account only. Other purposes
  // stay type-only. The gateway does not evaluate this SQL on the request
  // path — it reads `accounts.credit_limit_usd_micros`. Every event that
  // changes the overlay must rewrite that column (or wait for the #1998
  // drift recon). Exemption expiry is the one time-predicate that cannot
  // fire a rewrite: SQL drops to 0 at expires_at, the column stays until
  // drift recon (~5 minutes). There is no exemption-expiry sweeper.
  return `(CASE
    WHEN ${creditSpendabilityCountsPredicate(accountIdSql, nowSql)}
    THEN ${typeWindow}
    ELSE 0
  END)`;
}

/**
 * Spendability overlay: AND-ed only onto the spendability window so
 * D+7 / collections evaluate that window to 0 unless an exemption is
 * still live. Single definition. `credit-overdue.ts` does not re-export
 * the name because `index.ts` already `export *`s this module.
 */
export function creditSpendabilityCountsPredicate(
  accountIdSql: CreditLimitAccountIdSql,
  nowSql = 'CURRENT_TIMESTAMP',
): string {
  return `NOT EXISTS (
    SELECT 1
      FROM credit_overdue_states s
     WHERE s.account_id = ${accountIdSql}
       AND s.stage IN ('stopped', 'collections')
       AND NOT EXISTS (
         SELECT 1
           FROM credit_emergency_exemptions e
          WHERE e.account_id = ${accountIdSql}
            AND e.expires_at > ${nowSql}
       )
  )`;
}

/**
 * EXISTS predicate: the account currently has at least one effective
 * grant this purpose counts. Used by the monthly-statement scan (long
 * term only) and the settlement credit-account probe.
 */
export function hasEffectiveCreditSql(
  purpose: CreditLimitPurpose,
  accountIdSql: CreditLimitAccountIdSql,
): string {
  const typeList = creditGrantTypesSqlInList(purpose);
  return `EXISTS (
    SELECT 1
      FROM credit_grants g
     WHERE g.account_id = ${accountIdSql}
       AND g.status = 'active'
       AND g.grant_type IN (${typeList})
       AND g.starts_at <= CURRENT_TIMESTAMP
       AND (g.ends_at IS NULL OR g.ends_at > CURRENT_TIMESTAMP)
  )`;
}

// ─── Persisted grant row (wire + store) ────────────────────────────────────

export interface CreditGrant {
  readonly id: string;
  readonly account_id: string;
  readonly grant_type: CreditGrantType;
  readonly amount_usd_micros: number;
  /** Operator CNY original locked at approval; null on pre-cutover rows. */
  readonly amount_cny_cents: number | null;
  /** USD_CNY rate used to lock `amount_usd_micros`; null on pre-cutover rows. */
  readonly applied_rate: number | null;
  readonly status: CreditGrantStatus;
  readonly starts_at: string;
  readonly ends_at: string | null;
  readonly ended_at: string | null;
  readonly end_reason: CreditGrantEndReason | null;
  readonly approval_request_id: string | null;
  readonly requested_by_ops_user_id: string | null;
  readonly approved_by_ops_user_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export function creditGrantToSnapshot(grant: CreditGrant): CreditGrantSnapshot {
  return {
    grantType: grant.grant_type,
    amountUsdMicros: grant.amount_usd_micros,
    status: grant.status,
    startsAt: grant.starts_at,
    endsAt: grant.ends_at,
  };
}
